import { useCallback, useContext, useMemo } from 'react';
import type { AccountPersona } from '@/api/types';
import { AuthContext, type AuthContextValue, type OAuthSignInOutcome } from './AuthContext';
import type { Workspace } from './WorkspaceContext';

/**
 * `useAuth` extiende el `AuthContext` crudo con la lista de `workspaces` del usuario.
 *
 * Hoy el backend V1 expone **un solo tenant por usuario** (vía `/api/auth/me` y el login), así
 * que `workspaces` tiene exactamente un elemento derivado del usuario real autenticado. Cuando V2
 * implemente memberships (un contador con N clientes), esta función pasará a leer la lista real de
 * tenants del usuario; el resto de la app (WorkspaceContext/Switcher/Sidebar) ya está preparada
 * para `length > 1`.
 *
 * (Antes esto era un mock de identidad demo gustavo/lucia con workspaces hardcodeados; se removió
 * cuando el login real reemplazó al demo.)
 */
export interface AuthContextValueExtended extends AuthContextValue {
  workspaces: Workspace[];
  /** Slice I — login con Google id_token. `persona` opcional la pasa el alta comercial. */
  signInWithGoogle: (idToken: string, persona?: AccountPersona) => Promise<OAuthSignInOutcome>;
  /** Slice I — login con Microsoft id_token. `persona` opcional la pasa el alta comercial. */
  signInWithMicrosoft: (idToken: string, persona?: AccountPersona) => Promise<OAuthSignInOutcome>;
}

export function useAuth(): AuthContextValueExtended {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider.');
  }

  const { user } = ctx;

  // Un único workspace derivado del tenant real del usuario. Sin usuario → lista vacía.
  const workspaces = useMemo<Workspace[]>(() => {
    if (!user) return [];
    return [
      {
        id: user.tenantId,
        name: user.tenantName,
        cuit: '',
        role: user.role,
        status: 'Active',
        alerts: { count: 0, severity: 'info', reasons: [] },
        monthlyVolume: 0,
        pendingDrafts: 0,
        lastActivity: '',
        certExpiresAt: null,
        planLabel: 'Trial',
      },
    ];
  }, [user]);

  const signInWithGoogle = useCallback(
    (idToken: string, persona?: AccountPersona): Promise<OAuthSignInOutcome> =>
      ctx.signInWithProvider('Google', idToken, persona),
    [ctx],
  );
  const signInWithMicrosoft = useCallback(
    (idToken: string, persona?: AccountPersona): Promise<OAuthSignInOutcome> =>
      ctx.signInWithProvider('Microsoft', idToken, persona),
    [ctx],
  );

  return {
    ...ctx,
    workspaces,
    signInWithGoogle,
    signInWithMicrosoft,
  };
}
